import { inject, injectable, injectFromBase } from 'inversify'
import type { Router } from 'vue-router'
// TODO: Fix ignored tp/forbid-import-composable-to-service
// eslint-disable-next-line tp/forbid-import-composable-to-service,tp/using-vue-in-services-restriction
import type { Plugin } from '@/composition/vue/compositionApi'
import {
  type ExternalTrackerAsPlugin,
  type TrackableConfig,
  zendeskCountries,
  zendeskVisibleRoutes,
  hideZendeskWidgetBreakpoint,
  type ZendeskMessagingToken,
} from '@/services/tracking/types'
import ConfigReader from '@/core/configReader'
import { SERVICE_TYPES } from '@/core/container/types'
import type RouterService from '@/services/route/routerService'
import type SubscriptionService from '@/services/transport/subscriptionService'
import { type Route, ROUTER_AFTER_EACH } from '@/services/route/types'
import type UserService from '@/services/domain/user/userService'
import type User from '@/data/models/domain/User'
import { UserStatus } from '@/services/domain/user/types'
import ExternalTrackerService from '@/services/tracking/externalTrackerService'
import type UnauthenticatedCountryService from '@/services/domain/unauthenticatedCountryService'
import type UnauthenticatedCountry from '@/data/models/domain/UnauthenticatedCountry'
import type LoggedInStatusService from '@/services/auth/loggedInStatusService'
import type WindowService from '@/services/browser/windowService'
import type LoggerService from '@/services/loggerService'
import TmLogicError from '@/core/error/tmLogicError'
import type { FeatureFlagsService } from '@/services/featureFlagsService'
import { FeatureFlags } from '@/services/types'
import type { HttpService } from '@/services/transport/httpService'
import type { AbstractEndpointsInterface } from '@/services/endpointsService'
import type { MonitoringLoggerServiceInterface } from '@/services/monitoring/types'
import { LogLevel } from '@/core/logger/types'
import { wait } from '@/utils/async'

const LOGIN_CONFIRMATION_TIMEOUT = 5000

@injectable()
@injectFromBase()
export default class ZendeskService extends ExternalTrackerService {
  constructor(
    @inject(SERVICE_TYPES.RouterService) protected readonly routerService: RouterService,
    @inject(SERVICE_TYPES.WindowService) protected readonly windowService: WindowService,
    @inject(SERVICE_TYPES.UserService) protected readonly userService: UserService,
    @inject(SERVICE_TYPES.SubscriptionService) protected readonly subscriptionService: SubscriptionService,
    @inject(SERVICE_TYPES.LoggerService) protected readonly loggerService: LoggerService,
    @inject(SERVICE_TYPES.UnauthenticatedCountryService)
    protected readonly unauthenticatedCountryService: UnauthenticatedCountryService,
    @inject(SERVICE_TYPES.LoggedInStatusService) protected readonly loggedInStatusService: LoggedInStatusService,
    @inject(SERVICE_TYPES.FeatureFlagsService) protected readonly featureFlagsService: FeatureFlagsService,
    @inject(SERVICE_TYPES.Api) protected readonly api: HttpService,
    @inject(SERVICE_TYPES.EndpointsService) protected readonly endpointsService: AbstractEndpointsInterface,
    @inject(SERVICE_TYPES.MonitoringLoggerService)
    protected readonly monitoringLoggerService: MonitoringLoggerServiceInterface,
  ) {
    super()
  }

  private widgetPromise: Promise<void>

  private isUserLoggedIntoWidget = false

  private isLoginConfirmed = false

  private loginAttempt: Promise<void> = Promise.resolve()

  private loginGeneration = 0

  private async callWidget(...args: unknown[]): Promise<void> {
    const widget = await this.getWidget()
    widget?.(...args)
  }

  private async getWidget() {
    await this.widgetPromise
    const widget = this.windowService.get<((...args: any[]) => any) | undefined>('zE')

    if (!widget) {
      this.loggerService.error('error', 'Zendesk widget is not loaded')
      return null
    }

    return widget
  }

  public getConfig(): TrackableConfig {
    const config = ConfigReader.config()

    return {
      id: config.tracking.zendeskKey,
    }
  }

  public getPlugin(): Plugin {
    return {
      install: () => this.initialize(),
    } as Plugin
  }

  public async initialize(): Promise<void> {
    const key = this.getConfig().id
    const router: Router = this.routerService.getRouter()
    let isInitialized = false
    const track = async () => {
      if (await this.shouldLoadWidget(router.currentRoute.value)) {
        if (!isInitialized) {
          this.widgetPromise = this.initializeTrackerAsPlugin(trackerAsPlugin)
          isInitialized = true
          await this.setUpOptions()
        }
        if (this.loggedInStatusService.isUserLoggedIn()) {
          await this.trackAuthenticated()
        } else {
          await this.trackUnauthenticated()
        }
      } else if (isInitialized) {
        await this.hideWidget()
      }
    }
    const trackingHandler = async () => {
      await router.isReady()
      await track()
      this.subscriptionService.subscribe(ROUTER_AFTER_EACH, track, true)
    }

    const trackerAsPlugin = {
      src: `https://static.zdassets.com/ekr/snippet.js?key=${key}`,
      id: 'ze-snippet',
      trackingHandler,
    } as ExternalTrackerAsPlugin

    await trackingHandler()
  }

  protected async trackAuthenticated(): Promise<void> {
    await this.loginZendeskUser()
    return this.showWidget()
  }

  protected trackUnauthenticated(): Promise<void> {
    return this.showWidget()
  }

  protected async shouldLoadWidget(route: Route): Promise<boolean> {
    const isActiveFeatureFlag = this.featureFlagsService.isFeatureEnabled(FeatureFlags.ZendeskWidget)
    const isVisibleRoute: boolean =
      isActiveFeatureFlag && !!route.name && zendeskVisibleRoutes.includes(String(route.name))
    if (this.windowService.self().screen.width < hideZendeskWidgetBreakpoint) {
      return false
    }
    if (this.loggedInStatusService.isUserLoggedIn()) {
      try {
        const user: User = this.userService.currentUser()
        const isUnsupportedUserCountry = !zendeskCountries.includes(user.countryId)
        const isUserWizard = user.status === UserStatus.ST_WIZARD
        if (isUnsupportedUserCountry || isUserWizard) {
          return false
        }

        return isVisibleRoute
      } catch (e) {
        if (e instanceof TmLogicError) {
          return false
        }
        throw e
      }
    }

    const country: UnauthenticatedCountry | null = await this.unauthenticatedCountryService.loadCountry()

    return !!country?.id && zendeskCountries.includes(country.id) && isVisibleRoute
  }

  protected async setUpOptions(): Promise<void> {
    await this.callWidget('messenger:set', 'cookies', 'functional')
  }

  protected async loginZendeskUser(): Promise<void> {
    if (this.isUserLoggedIntoWidget) {
      return this.loginAttempt
    }

    const widget = await this.getWidget()
    if (!widget) {
      return
    }

    this.isUserLoggedIntoWidget = true

    const generation = this.loginGeneration
    const isStale = () => generation !== this.loginGeneration

    let settleLoginAttempt: () => void = () => {}

    this.loginAttempt = Promise.race([
      new Promise<void>((resolve) => {
        settleLoginAttempt = resolve
      }),
      wait(LOGIN_CONFIRMATION_TIMEOUT).then(() => undefined),
    ])

    widget(
      'messenger',
      'loginUser',
      // Re-invoked by the widget on 401
      async (setToken: (token: string) => void) => {
        try {
          const token = await this.fetchMessagingToken()
          if (isStale()) {
            settleLoginAttempt()
            return
          }

          setToken(token)
        } catch (e) {
          if (isStale()) {
            settleLoginAttempt()
            return
          }

          const subchannel = this.isLoginConfirmed ? 'zendeskTokenRefresh' : 'zendeskMessagingToken'
          this.allowLoginRetry()
          this.captureLoginError(e, subchannel)
          settleLoginAttempt()
        }
      },
      (error: unknown) => {
        if (isStale()) {
          settleLoginAttempt()
          return
        }

        if (!error) {
          this.isLoginConfirmed = true
          settleLoginAttempt()
          return
        }

        // Token rejected:
        // 'zendeskLoginUser' - on first login
        // 'zendeskLoginRefresh' - on refresh
        const subchannel = this.isLoginConfirmed ? 'zendeskLoginRefresh' : 'zendeskLoginUser'
        this.allowLoginRetry()
        this.captureLoginError(error, subchannel)
        settleLoginAttempt()
      },
    )

    return this.loginAttempt
  }

  private allowLoginRetry(): void {
    if (!this.isLoginConfirmed) {
      this.isUserLoggedIntoWidget = false
    }
  }

  private captureLoginError(e: unknown, subchannel: string): void {
    const reported = e instanceof Error ? e : new Error(`${subchannel} failed: ${JSON.stringify(e)}`)

    this.monitoringLoggerService.logAndCapture(reported, LogLevel.ERROR, subchannel)
  }

  private async fetchMessagingToken(): Promise<string> {
    const { data } = await this.api.get<ZendeskMessagingToken>(this.endpointsService.getPath('zendeskMessagingToken'))

    return data.token
  }

  private async showWidget(): Promise<void> {
    await this.callWidget('messenger', 'show')
  }

  private async hideWidget(): Promise<void> {
    await this.callWidget('messenger', 'hide')
  }

  private async logout(): Promise<void> {
    this.loginGeneration += 1
    this.isUserLoggedIntoWidget = false
    this.isLoginConfirmed = false
    await this.callWidget('messenger', 'logoutUser')
  }

  public cleanUp(): void {
    this.logout()
  }
}
